import React, { useState } from "react";
import { Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input, Select } from "@/components/Field";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

const ROLES = ["customer", "support", "developer", "project_manager", "admin", "super_admin"];

export default function Customers() {
  const { t } = useI18n();
  const { items: users, loading, reload: load } = useAdminData("/users");
  const [filter, setFilter] = useState("customer");
  const [editing, setEditing] = useState(null);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/users/${form.id}`, form);
      else await api.post("/users", form);
      toast.success("OK"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const list = users.filter((u) => filter === "all" ? true : filter === "staff" ? u.role !== "customer" : u.role === "customer");
  const del = async (u) => {
    if (!window.confirm(`Kunde „${u.name || u.email}" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Kunde gelöscht"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl font-black">{t("admin.customers")}</h1>
        <div className="flex gap-2">
          {["customer", "staff", "all"].map((x) => (
            <button key={x} onClick={() => setFilter(x)} className={`rounded-full px-4 py-1.5 text-sm font-semibold border ${filter === x ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`cust-filter-${x}`}>{x}</button>
          ))}
          <button onClick={() => setEditing({ role: "developer" })} className="btn-primary !py-1.5 !px-4 text-sm" data-testid="user-new"><Plus size={16} /> Team</button>
        </div>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground"><tr><th className="p-4">{t("common.name")}</th><th className="p-4">{t("common.email")}</th><th className="p-4">Firma</th><th className="p-4">Rolle</th><th className="p-4 text-right">{t("common.actions")}</th></tr></thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={5} /> : list.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-${u.id}`}>
                <td className="p-4 font-medium">{u.name}</td><td className="p-4 text-muted-foreground">{u.email}</td><td className="p-4">{u.company || "—"}</td>
                <td className="p-4"><span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold">{u.role}</span></td>
                <td className="p-4 text-right"><div className="flex gap-1 justify-end"><button onClick={() => setEditing(u)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`user-edit-${u.id}`}>{t("common.edit")}</button><button onClick={() => del(u)} className="btn-ghost !py-1.5 !px-2.5 text-xs text-red-500 hover:text-red-600" data-testid={`user-delete-${u.id}`}><Trash2 size={13} /></button></div></td>
              </tr>
            ))}
            {!loading && list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <Drawer u={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function Drawer({ u, onClose, onSave }) {
  const { t } = useI18n();
  const [f, setF] = useState({ id: u.id, name: u.name || "", email: u.email || "", company: u.company || "", phone: u.phone || "", role: u.role || "developer", active: u.active ?? true, password: "" });
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="relative w-full max-w-md bg-background border-l border-border h-full overflow-y-auto p-6 space-y-4" data-testid="user-drawer">
        <div className="flex items-center justify-between"><h2 className="font-display text-xl font-black">{u.id ? t("common.edit") : "Team"}</h2><button type="button" onClick={onClose}><X size={20} /></button></div>
        <Field label={t("common.name")}><Input testid="u-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={t("common.email")}><Input testid="u-email" type="email" required disabled={!!u.id} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Firma"><Input testid="u-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></Field>
        <Field label="Rolle"><Select testid="u-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</Select></Field>
        {!u.id && <Field label="Passwort"><Input testid="u-password" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="changeme123" /></Field>}
        <label className="flex items-center gap-2.5 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="h-4 w-4 accent-[hsl(21,100%,50%)]" data-testid="u-active" /> Aktiv</label>
        <button className="btn-primary w-full" data-testid="user-save">{t("common.save")}</button>
      </form>
    </div>
  );
}
